import { useState, useEffect, useCallback, useRef } from "react";
import { api, CURRENCIES, formatPrice } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Check, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ReferralCard } from "../components/ReferralCard";

const loadScript = (src, id) => new Promise((resolve, reject) => {
  if (document.getElementById(id)) {
    // Wait until window.paypal is ready
    if (id === "paypal-sdk" && !window.paypal) {
      const int = setInterval(() => { if (window.paypal) { clearInterval(int); resolve(true); } }, 80);
      setTimeout(() => { clearInterval(int); resolve(true); }, 5000);
      return;
    }
    return resolve(true);
  }
  const s = document.createElement("script");
  s.src = src; s.id = id; s.async = true;
  s.onload = () => resolve(true);
  s.onerror = () => reject(new Error("Failed to load " + src));
  document.body.appendChild(s);
});

const PayPalButtonsInline = ({ pkg, clientId, onSuccess }) => {
  const containerRef = useRef(null);
  const renderedRef = useRef(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Always load from the production SDK host — client-id determines the environment.
      await loadScript(
        `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD&intent=capture`,
        "paypal-sdk",
      );
      if (cancelled || !window.paypal || renderedRef.current || !containerRef.current) return;
      renderedRef.current = true;
      setReady(true);
      try {
        await window.paypal.Buttons({
          style: { layout: "vertical", color: "gold", shape: "pill", label: "paypal", height: 44 },
          createOrder: async () => {
            const { data } = await api.post("/payments/paypal/order", { package_id: pkg.id });
            return data.order_id;
          },
          onApprove: async (data) => {
            try {
              const { data: cap } = await api.post("/payments/paypal/capture", { order_id: data.orderID });
              if (cap.payment_status === "paid") {
                toast.success(`${pkg.credits} credits added`);
                onSuccess(cap.credits);
              } else {
                toast.error("Payment not completed");
              }
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Payment capture failed");
            }
          },
          onCancel: () => toast.info("Payment cancelled"),
          onError: (err) => {
            console.warn("PayPal error", err);
            toast.error("Payment error — please try again");
          },
        }).render(containerRef.current);
      } catch (err) {
        console.warn("PayPal render failed", err);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, pkg, onSuccess]);

  return (
    <div>
      {!ready && (
        <div className="h-11 rounded-full border border-white/10 bg-black/40 flex items-center justify-center text-xs text-neutral-500">
          Loading PayPal…
        </div>
      )}
      <div ref={containerRef} data-testid={`paypal-buttons-${pkg.id}`} />
    </div>
  );
};

const Pricing = () => {
  const { user, refresh } = useAuth();
  const [currency, setCurrency] = useState("USD");
  const [loading, setLoading] = useState(null);
  const [region, setRegion] = useState(null); // { provider, country, ... }
  const [manualOverride, setManualOverride] = useState(
    () => localStorage.getItem("preferred_region") || null
  );

  useEffect(() => {
    // Client-side geolocation fallback: run before server call
    const query = manualOverride ? `?force=${manualOverride}` : "";
    api.get(`/payments/region${query}`)
      .then(({ data }) => setRegion(data))
      .catch(() => setRegion({ provider: "paypal", country: "UNKNOWN", paypal_client_id: "" }));
  }, [manualOverride]);

  const setPreferredRegion = (code) => {
    if (code) {
      localStorage.setItem("preferred_region", code);
    } else {
      localStorage.removeItem("preferred_region");
    }
    setManualOverride(code);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = useCallback(() => {
    const redirectUrl = window.location.origin + "/pricing";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const buyRazorpay = async (packageId, pkg) => {
    setLoading(packageId);
    try {
      await loadScript("https://checkout.razorpay.com/v1/checkout.js", "razorpay-sdk");
      const { data } = await api.post("/payments/razorpay/order", { package_id: packageId });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Numero by Auracean",
        description: pkg.label,
        order_id: data.order_id,
        prefill: { name: data.name, email: data.email },
        theme: { color: "#D4AF37" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              order_id: resp.razorpay_order_id,
              payment_id: resp.razorpay_payment_id,
              signature: resp.razorpay_signature,
            });
            await refresh();
            toast.success(`${pkg.credits} credits added`);
            setTimeout(() => (window.location.href = "/payment/success?verified=1"), 400);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Verification failed");
          }
        },
        modal: { ondismiss: () => setLoading(null) },
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setLoading(null);
    }
  };

  const buyPaypal = null; // Replaced by <PayPalButtonsInline/> — rendered directly per card

  const onPaypalSuccess = useCallback(async () => {
    await refresh();
    setTimeout(() => (window.location.href = "/payment/success?verified=1"), 400);
  }, [refresh]);

  const buy = async (packageId, pkg) => {
    if (!user) { handleLogin(); return; }
    if (region?.provider === "razorpay") return buyRazorpay(packageId, pkg);
    // PayPal path uses <PayPalButtonsInline/> — this button is disabled/hidden
  };

  const packages = [
    { id: "starter", credits: 10, usd: 4.99, inr: 399, label: "Starter",
      features: ["10 questions worth of credits", "All reading depths", "Full archive access"],
      highlight: false },
    { id: "premium", credits: 25, usd: 9.99, inr: 799, label: "Seer",
      features: ["25 questions worth of credits", "Best value — 20% more", "Full archive access", "Priority reading response"],
      highlight: true },
  ];

  const showInr = region?.provider === "razorpay";
  const providerLabel = region?.provider === "razorpay" ? "Razorpay · UPI / Cards / NetBanking"
                     : region?.provider === "paypal" ? "PayPal · Cards worldwide" : "";

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-16">
      <div className="text-center mb-12 sm:mb-16">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Credits</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-4">
          Pay in gold, <span className="italic">not in gods.</span>
        </h1>
        <p className="text-neutral-400 max-w-xl mx-auto text-sm sm:text-base">
          Credits never expire. Every question deducts by depth — the deeper the enquiry, the greater the toll.
        </p>
        {region && (
          <p className="mt-4 text-xs uppercase tracking-[0.25em] text-neutral-500" data-testid="provider-label">
            Payment via {providerLabel}
          </p>
        )}
      </div>

      {/* Manual region override — fallback if geo detection is wrong */}
      <div className="flex justify-center mb-6 sm:mb-8">
        <div className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/40 p-1" data-testid="region-toggle">
          <button
            onClick={() => setPreferredRegion(null)}
            data-testid="region-auto"
            className={`px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm transition-colors ${
              !manualOverride ? "bg-gold text-black" : "text-neutral-400 hover:text-white"
            }`}
          >Auto-detect</button>
          <button
            onClick={() => setPreferredRegion("IN")}
            data-testid="region-in"
            className={`px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm transition-colors ${
              manualOverride === "IN" ? "bg-gold text-black" : "text-neutral-400 hover:text-white"
            }`}
          >India (INR)</button>
          <button
            onClick={() => setPreferredRegion("US")}
            data-testid="region-us"
            className={`px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm transition-colors ${
              manualOverride === "US" ? "bg-gold text-black" : "text-neutral-400 hover:text-white"
            }`}
          >Worldwide (USD)</button>
        </div>
      </div>

      {!showInr && (
        <div className="flex justify-center mb-8 sm:mb-12">
          <div className="flex items-center gap-3 rounded-full border border-white/10 bg-black/40 px-4 py-2">
            <span className="text-sm text-neutral-500">Show prices in</span>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="currency-select" className="h-8 w-24 border-0 bg-transparent text-white focus:ring-0 focus:ring-offset-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141419] border-white/10 text-white">
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`currency-${c.code}`}>{c.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="grid gap-5 sm:gap-6 md:grid-cols-2 max-w-4xl mx-auto">
        {packages.map((p) => (
          <div
            key={p.id}
            data-testid={`pricing-tier-${p.id}`}
            className={`relative rounded-2xl border p-6 sm:p-10 ${
              p.highlight ? "border-gold/40 bg-[#141419] shadow-gold" : "border-white/5 bg-[#141419]"
            } hover:border-gold/50 transition-colors duration-300`}
          >
            {p.highlight && (
              <div className="absolute -top-3 left-6 sm:left-10 rounded-full bg-gold px-3 py-1 text-xs uppercase tracking-[0.2em] text-black">
                Best value
              </div>
            )}
            <div className="flex items-baseline gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-gold" />
              <span className="text-xs uppercase tracking-[0.25em] text-neutral-500">{p.label}</span>
            </div>
            <div className="font-serif text-5xl sm:text-6xl tracking-tighter mb-1">
              <span data-testid={`price-${p.id}`}>
                {showInr ? `₹${p.inr}` : formatPrice(p.usd, currency)}
              </span>
            </div>
            <div className="text-neutral-400 mb-6 sm:mb-8 text-sm">
              {showInr ? `≈ $${p.usd.toFixed(2)} USD` : `≈ $${p.usd.toFixed(2)} USD`}
            </div>

            <div className="mb-6 sm:mb-8">
              <div className="flex items-baseline gap-2">
                <span className="font-serif text-4xl sm:text-5xl text-gold">{p.credits}</span>
                <span className="text-neutral-400">credits</span>
              </div>
            </div>

            <ul className="space-y-3 mb-8 sm:mb-10">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-neutral-300">
                  <Check className="h-4 w-4 text-gold shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <Button
              onClick={() => buy(p.id, p)}
              disabled={loading === p.id || !region || region?.provider === "paypal"}
              data-testid={`buy-${p.id}`}
              className={`w-full rounded-full py-5 sm:py-6 transition-colors duration-200 ${
                p.highlight
                  ? "bg-gold text-black hover:bg-[#F3E5AB]"
                  : "border border-white/20 bg-transparent text-white hover:bg-white/5"
              } ${region?.provider === "paypal" ? "hidden" : ""}`}
            >
              {loading === p.id ? "Opening payment…"
                : region ? `Buy ${p.credits} credits`
                : "Loading…"}
            </Button>
            {region?.provider === "paypal" && user && region.paypal_client_id && (
              <PayPalButtonsInline
                pkg={p}
                clientId={region.paypal_client_id}
                onSuccess={onPaypalSuccess}
              />
            )}
            {region?.provider === "paypal" && !user && (
              <Button
                onClick={handleLogin}
                data-testid={`signin-${p.id}`}
                className="w-full rounded-full py-5 sm:py-6 bg-gold text-black hover:bg-[#F3E5AB]"
              >
                Sign in to buy
              </Button>
            )}
          </div>
        ))}
      </div>

      <p className="text-center text-xs text-neutral-500 mt-10 sm:mt-12">
        Secure payment via {region?.provider === "razorpay" ? "Razorpay" : "PayPal"}
      </p>

      {user && (
        <div className="mt-14 sm:mt-20 max-w-3xl mx-auto">
          <ReferralCard />
        </div>
      )}
    </div>
  );
};

export default Pricing;
